/**
 * EPCFormWizard Component
 * Multi-step wizard for creating and editing EPC data
 *
 * @version 2025.2.0
 */

import React from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Save as SaveIcon,
  SkipNext as SkipIcon,
} from '@mui/icons-material';
import { EpcFormData, TipoDeRisco } from '../../../../types/epc';
import { centsToReais, reaisToCents } from '../../../../utils/financialHelpers';
import { EPCFormWizardProps } from './EPCFormWizard.types';
import { useEPCFormWizard } from './hooks/useEPCFormWizard';
import { IdentificationStep } from './steps/IdentificationStep';
import { TechnicalStep } from './steps/TechnicalStep';
import { StockStep } from './steps/StockStep';

// Full validation schema (for final submission)
const fullValidationSchema = yup.object({
  nome: yup
    .string()
    .required('Nome do EPC é obrigatório')
    .min(3, 'Mínimo de 3 caracteres')
    .max(255, 'Máximo de 255 caracteres'),
  tipoDeRisco: yup
    .string()
    .oneOf(Object.values(TipoDeRisco), 'Tipo de risco inválido')
    .required('Tipo de risco é obrigatório'),
  risco: yup
    .string()
    .required('Descrição do risco é obrigatória')
    .max(255, 'Máximo de 255 caracteres'),
  validade: yup.string().required('Data de validade é obrigatória'),
  revisao: yup.string().max(50, 'Máximo de 50 caracteres'),
  atenuacao: yup.string().max(100, 'Máximo de 100 caracteres'),
  proporcional: yup.string().max(50, 'Máximo de 50 caracteres'),
  metodo: yup.string().max(255, 'Máximo de 255 caracteres'),
  custo: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(0, 'Custo não pode ser negativo'),
  quantidade: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(0, 'Quantidade não pode ser negativa')
    .integer('Quantidade deve ser um número inteiro'),
  unidade: yup.string().max(50, 'Máximo de 50 caracteres'),
  observacao: yup.string().max(2000, 'Máximo de 2000 caracteres'),
});

export const EPCFormWizard: React.FC<EPCFormWizardProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const methods = useForm<EpcFormData>({
    resolver: yupResolver(fullValidationSchema as yup.ObjectSchema<EpcFormData>),
    defaultValues: {
      nome: initialData?.nome || '',
      tipoDeRisco: initialData?.tipoDeRisco || TipoDeRisco.FISICO,
      risco: initialData?.risco || '',
      efficiency: initialData?.efficiency || undefined,
      brandModel: initialData?.brandModel || '',
      validade: initialData?.validade || '',
      revisao: initialData?.revisao || '',
      atenuacao: initialData?.atenuacao || '',
      proporcional: initialData?.proporcional || '',
      metodo: initialData?.metodo || '',
      custo: centsToReais(initialData?.custo),
      quantidade: initialData?.quantidade || undefined,
      unidade: initialData?.unidade || '',
      observacao: initialData?.observacao || '',
    },
    mode: 'onBlur',
  });

  const isEditing = Boolean(initialData?.id);

  return (
    <FormProvider {...methods}>
      <EPCFormWizardContent
        onSubmit={onSubmit}
        onCancel={onCancel}
        isSubmitting={isSubmitting}
        isEditing={isEditing}
      />
    </FormProvider>
  );
};

interface WizardContentProps {
  onSubmit: (data: EpcFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  isEditing: boolean;
}

const EPCFormWizardContent: React.FC<WizardContentProps> = ({
  onSubmit,
  onCancel,
  isSubmitting,
  isEditing,
}) => {
  const { getValues } = useFormContext<EpcFormData>();

  const {
    state,
    steps,
    isFirstStep,
    isLastStep,
    currentStepKey,
    canSkip,
    handleNext,
    handleBack,
    handleSkip,
    setError,
  } = useEPCFormWizard();

  const disabled = isSubmitting || state.loading;

  // Handle form submission
  const handleFormSubmit = async () => {
    // Validate current step first
    const isValid = await handleNext();

    if (!isValid) return;

    // If we're on the last step and validation passed, submit
    if (isLastStep) {
      try {
        const data = getValues();
        // Convert custo from reais to centavos if provided
        const submissionData = {
          ...data,
          custo: reaisToCents(data.custo),
        };
        await onSubmit(submissionData);
      } catch (err: unknown) {
        const error = err as { message?: string };
        setError(error.message ?? 'Erro ao salvar EPC');
      }
    }
  };

  // Handle back button
  const handleBackClick = () => {
    if (isFirstStep) {
      onCancel();
    } else {
      handleBack();
    }
  };

  // Handle next button
  const handleNextClick = async () => {
    if (isLastStep) {
      await handleFormSubmit();
    } else {
      await handleNext();
    }
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStepKey) {
      case 'identification':
        return <IdentificationStep disabled={disabled} />;
      case 'technical':
        return <TechnicalStep disabled={disabled} />;
      case 'stock':
        return <StockStep disabled={disabled} />;
      default:
        return null;
    }
  };

  // Get button text
  const getNextButtonText = () => {
    if (isSubmitting) return 'Salvando...';
    if (state.loading) return 'Validando...';
    if (isLastStep) return isEditing ? 'Atualizar EPC' : 'Salvar EPC';
    return 'Próximo';
  };

  // Get button icon
  const getNextButtonIcon = () => {
    if (isSubmitting || state.loading) return <CircularProgress size={20} />;
    if (isLastStep) return <SaveIcon />;
    return <NextIcon />;
  };

  return (
    <Box>
      {/* Stepper */}
      <Stepper activeStep={state.activeStep} sx={{ mb: 4 }}>
        {steps.map((step) => (
          <Step key={step.key}>
            <StepLabel optional={step.optional ? 'Opcional' : undefined}>
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Error Alert */}
      {state.error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {state.error}
        </Alert>
      )}

      {/* Step Content */}
      <Box sx={{ minHeight: 300, mb: 4 }}>{renderStepContent()}</Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          onClick={handleBackClick}
          disabled={disabled}
          startIcon={!isFirstStep && <BackIcon />}
          variant="outlined"
        >
          {isFirstStep ? 'Cancelar' : 'Voltar'}
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Skip button for optional steps */}
          {canSkip && !isLastStep && (
            <Button
              onClick={handleSkip}
              disabled={disabled}
              startIcon={<SkipIcon />}
            >
              Pular
            </Button>
          )}

          {/* Next/Submit button */}
          <Button
            onClick={handleNextClick}
            variant="contained"
            color={isLastStep ? 'success' : 'primary'}
            disabled={disabled}
            endIcon={getNextButtonIcon()}
          >
            {getNextButtonText()}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EPCFormWizard;
